import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import useAuthState, { Tenant } from './auth.state';
import { axiosInstance } from '@/utils/lib/axios';
import { toast } from 'react-toastify';
import { env } from '@/utils/constants/env';

type Store = {
  currentTenant: Tenant | null;
  setCurrentTenant: (tenant: Tenant | null) => void;
  removeCurrentTenant: () => void;
  createNewWorkspace: (name: string) => void;
  homepageCreateNewWorkspace: boolean;
  setHomepageCreateNewWorkspace: (value: boolean) => void;
};

const useTenantState = create<Store>()(
  persist(
    set => ({
      currentTenant: null,
      homepageCreateNewWorkspace: false,
      setCurrentTenant: (tenant: Tenant | null) => {
        set({ currentTenant: tenant });
        // Update favicon if tenant has faviconPath
        if (tenant?.faviconPath) {
          const faviconUrl = `${env.apiTasklistBaseUrl}${tenant.faviconPath}`;
          let link = document.querySelector('link#favicon') as HTMLLinkElement;
          if (!link) {
            link = document.createElement('link');
            link.id = 'favicon';
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = `${faviconUrl}?v=${Date.now()}`;
          console.log('Favicon updated on tenant switch:', link.href);
        }
      },
      removeCurrentTenant: () => set({ currentTenant: null }),
      createNewWorkspace: (name: string) => {
        const payload = {
          name: name,
        };
        // ✅ FIXED: Use relative path without env.apiTasklistBaseUrl
        // axiosInstance already has baseURL configured, and Vite proxy will handle routing
        axiosInstance
          .post(`/api/v1/tenant/tenants`, payload)
          .then(response => {
            console.log('Workspace created:', response.data);
            useAuthState.getState().loadTenants(); // Refresh tenants after creating a new workspace
            set({ homepageCreateNewWorkspace: false }); // Set the newly created workspace as the current tenant
            toast.success('Workspace created successfully', { position: 'top-right' });
          })
          .catch(error => {
            console.error('Error creating workspace:', error);
            // Show error details to user
            const errorMessage = error.response?.data?.message || error.message || 'Failed to create workspace';
            toast.error(errorMessage, { position: 'top-right' });
          });
      },
      setHomepageCreateNewWorkspace: (value: boolean) => set({ homepageCreateNewWorkspace: value }),
    }),
    {
      name: 'current-tenant-storage', // unique name for the storage
      storage: createJSONStorage(() => localStorage), // use localStorage
    },
  ),
);

export default useTenantState;